import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import {
  fetchEvaluatedOrdersWithHighScore,
  approveSelectedOrders
} from '../services/payoutService';

const PayoutApproverPage = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [subscriptionFilter, setSubscriptionFilter] = useState('All');

  const handleFetchOrders = async () => {
    setLoading(true);
    console.log('[PayoutApproverPage] Fetching orders...');
    try {
      const response = await fetchEvaluatedOrdersWithHighScore();
      console.log('[PayoutApproverPage] Fetched Orders Response:', response);

      if (Array.isArray(response) && response.length > 0) {
        setOrders(response);
      } else {
        console.error('[PayoutApproverPage] No valid orders data found.');
        setOrders([]);
      }
    } catch (err) {
      console.error('[PayoutApproverPage] Error fetching orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleApprove = async () => {
    const selectedIds = Object.keys(selectedOrders).filter((id) => selectedOrders[id]);
    try {
      await approveSelectedOrders(selectedIds);
      alert('Selected orders marked as completed.');
      await handleFetchOrders();
    } catch (err) {
      console.error('[PayoutApproverPage] Error approving orders:', err);
      alert('Error approving orders.');
    }
  };

  const isAnySelected = Object.values(selectedOrders).some((val) => val);

  const filteredOrders = subscriptionFilter === 'All'
    ? orders
    : orders.filter(order => order.subscription_plan_name === subscriptionFilter);

  const calculateTimeSinceSubmission = (submittedAt) => {
    if (!submittedAt) return 'N/A';
    const submittedDate = new Date(submittedAt);
    const now = new Date();
    const diffMs = now - submittedDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const diffMinutes = Math.floor((diffMs / (1000 * 60)) % 60);

    if (diffDays > 0) return `${diffDays}d ${diffHours}h ago`;
    if (diffHours > 0) return `${diffHours}h ${diffMinutes}m ago`;
    return `${diffMinutes}m ago`;
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-6 overflow-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Payout Approver Dashboard</h1>
        </div>

        <p className="text-gray-600 mb-4">
          Orders whose <strong>"order_status"</strong> is <strong>"Evaluated"</strong> and <strong>"cwe_final_score"</strong> is <strong>"65 or above"</strong> will be shown here.
        </p>

        <div className="flex gap-4 mb-4">
          <button
            onClick={handleFetchOrders}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow"
          >
            Fetch Orders
          </button>

          <select
            value={subscriptionFilter}
            onChange={(e) => setSubscriptionFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="All">All Plans</option>
            <option value="Basic">Basic</option>
            <option value="Standard">Standard</option>
            <option value="Premium">Premium</option>
          </select>
        </div>

        {loading && <p className="text-gray-600 mt-4">Loading...</p>}

        {filteredOrders.length === 0 && !loading && (
          <p className="text-red-600 mt-4">No orders found. Please try again later.</p>
        )}

        {filteredOrders.length > 0 && (
          <>
            {isAnySelected && (
              <button
                onClick={handleApprove}
                className="mb-4 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg shadow"
              >
                Approve
              </button>
            )}

            <table className="min-w-full bg-white border rounded shadow">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="px-4 py-2 border">Select</th>
                  <th className="px-4 py-2 border">Order ID</th>
                  <th className="px-4 py-2 border">Final Score</th>
                  <th className="px-4 py-2 border">Max Payout</th>
                  <th className="px-4 py-2 border">Payout Eligible</th>
                  <th className="px-4 py-2 border">Submitted At</th>
                  <th className="px-4 py-2 border">Time Since Submission</th>
                  <th className="px-4 py-2 border">Subscription Plan</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) => (
                  <tr key={order.order_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border">
                      <input
                        type="checkbox"
                        checked={!!selectedOrders[order.order_id]}
                        onChange={() => handleCheckboxChange(order.order_id)}
                      />
                    </td>
                    <td className="px-4 py-2 border">{order.order_id}</td>
                    <td className="px-4 py-2 border">{order.final_score || 'N/A'}</td>
                    <td className="px-4 py-2 border">{order.max_payout || 'N/A'}</td>
                    <td className="px-4 py-2 border">{order.payout_eligible_amount || 'N/A'}</td>
                    <td className="px-4 py-2 border">{order.submitted_at ? new Date(order.submitted_at).toLocaleString() : 'N/A'}</td>
                    <td className="px-4 py-2 border">{calculateTimeSinceSubmission(order.submitted_at)}</td>
                    <td className="px-4 py-2 border">{order.subscription_plan_name || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default PayoutApproverPage;
